import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/auth/LoginPage';
import RegisterPage from './components/auth/RegisterPage';
import CLIAuthorizePage from './components/auth/CLIAuthorizePage';
import ProtectedRoute, { DashboardRouteFallback } from './components/auth/ProtectedRoute';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import ContactPage from './components/ContactPage';
import LandingPage from './components/LandingPage';
import { useAuth } from './contexts/AuthContext';

const Dashboard = lazy(() => import('./Dashboard'));
const PricingPage = lazy(() => import('./components/PricingPage'));

/**
 * Marketing routes (`/`, `/about`). OAuth often completes on the Supabase Site URL (root) even when
 * `redirectTo` requests `/app`; once the session exists, send users into the app.
 */
const PublicHomeRoute: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth();
    if (!isLoading && isAuthenticated) {
        return <Navigate to="/app" replace />;
    }
    return <LandingPage />;
};

const DashboardRoute: React.FC<{ tryMode?: boolean }> = ({ tryMode }) => (
    <Suspense fallback={<DashboardRouteFallback />}>
        <Dashboard tryMode={tryMode} />
    </Suspense>
);

// Main App Component with Routing
const App: React.FC = () => {
    return (
        <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicHomeRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/cli/authorize" element={<CLIAuthorizePage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<PublicHomeRoute />} />
            <Route
                path="/pricing"
                element={
                    <ProtectedRoute>
                        <Suspense fallback={<DashboardRouteFallback />}>
                            <PricingPage />
                        </Suspense>
                    </ProtectedRoute>
                }
            />

            {/* Anonymous try (guest JWT) — must stay outside ProtectedRoute. Use /try/* so one
                Dashboard instance (avoids remount + new guest session when moving /try ↔ /try/projects/...). */}
            <Route path="/try/*" element={<DashboardRoute tryMode />} />

            {/* Protected routes — main app (lazy chunk: faster initial login/landing bundle) */}
            <Route
                path="/app"
                element={
                    <ProtectedRoute>
                        <DashboardRoute />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/projects/:projectId"
                element={
                    <ProtectedRoute>
                        <DashboardRoute />
                    </ProtectedRoute>
                }
            />

            {/* Catch-all: send unknown paths to app (auth will redirect to login if needed) */}
            <Route
                path="*"
                element={
                    <ProtectedRoute>
                        <DashboardRoute />
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
};

export default App;
